import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { LangToggle } from "@/components/LangToggle";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight, Download } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("Autentificare reușită"));
      navigate("/studio");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen klus-grid-bg flex flex-col items-center justify-center px-4 py-10 relative">
      <div className="absolute top-4 right-4"><LangToggle /></div>
      <Link to="/" className="mb-8"><Logo className="h-9" /></Link>
      <div className="w-full max-w-md animate-fade-up">
        <div className="bg-card border border-slate-800 rounded-3xl p-6 sm:p-8">
          <h1 className="text-3xl font-extrabold text-white mb-1">{t("Bine ai revenit")}</h1>
          <p className="text-slate-400 text-sm mb-6">{t("Autentifică-te pentru a genera randări.")}</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Email")}</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" className="mt-1.5 bg-slate-900 border-slate-700 h-12" placeholder="email@exemplu.ro" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-400">{t("Parolă")}</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" className="mt-1.5 bg-slate-900 border-slate-700 h-12" placeholder="••••••••" />
              <div className="text-right mt-2">
                <Link to="/forgot-password" className="text-xs text-slate-400 hover:text-primary" data-testid="forgot-link">
                  {t("Ai uitat parola?")}
                </Link>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full h-12 bg-primary hover:bg-primary/90 text-white text-base">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>{t("Autentificare")} <ArrowRight className="w-4 h-4 ml-1.5" /></>}
            </Button>
          </form>
          <p className="text-sm text-slate-400 mt-6 text-center">
            {t("Nu ai cont?")}{" "}
            <Link to="/register" className="text-primary hover:underline" data-testid="goto-register">{t("Creează cont")}</Link>
          </p>
          <p className="text-xs text-slate-500 mt-4 text-center">
            <Link to="/instalare" className="inline-flex items-center gap-1.5 hover:text-primary transition-colors" data-testid="goto-install">
              <Download className="w-3.5 h-3.5" /> {t("Instalează aplicația pe telefon sau PC")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
